import React from 'react';
import { NodeHeader } from './components/NodeHeader';
import { NodeContentWrapper } from './styles';
import { Body5, Container } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { Working } from '@ui/Working';
import styled from 'styled-components';
import { NodeWrapper } from './components/NodeWrapper';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { submitQueriesStream } from '@services/genaiQueriesV2';
import { logger, logNetRequest } from '@utils/usefulFuncs';
import { TrackingEvent } from '@classes/TrackingEvent';
import { showErrorBanner, showSuccessBanner } from '@utils/application';
import { AgentScriptGenerationHelpers } from '@classes/helpers/AgentScriptGenerationHelpers';
import { AI_TOOLS } from '@constants';
import { NODE_TYPES } from '@flow/nodes';
import OutputAggregationSourceHandle from './components/handles/OutputAggregationSourceHandle';
import PlanStepsSourceHandle from './components/handles/PlanStepsSourceHandle';
import QuerySourceHandle from './components/handles/QuerySourceHandle';
import { useUpdateNodeInternals } from '@xyflow/react';
import { useHasQueryNode, useIsAgentScriptEditable } from '@/contexts/FlowAgentContext';
import { useDispatch } from 'react-redux';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { EditIcon } from '@ui/buttons/EditButton';
import { addAdvancedSettingValues } from '@hooks/application/useAdvancedSettings';
import { ResultContextProvider } from '@/contexts/ResultContext';
import { AgentStreamingResultStatus } from '@features/result/AgentStreamingResult';
import { AgentAnswer } from '@features/result/components/AgentAnswer';
import { AgentExecutedPlan } from '@features/result/components/AgentExecutedPlan';
import { fetchAgentScripts, findAgentScriptByName } from '@features/agentScripting/agentScriptsApi';

const ContentWrapper = styled(NodeContentWrapper)`
  width: 80vw;
  max-width: 1000px;
  min-height: 180px;
  max-height: 90vh;
  overflow: auto;

  button {
    width: 160px;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
    -webkit-appearance: auto !important;
  }
`;
const LoadButtonWrapper = styled.div`
  padding: 16px 0 0 0;
  width: 100%;

  button {
    width: 100%;
  }
`;
const ButtonStyler = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;

  button {
    padding: 8px 8px !important;
    width: 104px;
  }
`;
const AgentNameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  input {
    height: 40px;
    width: 360px;
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    padding: 8px;
  }
`;
const EditAgentNameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 8px;
  height: 40px;

  border-radius: 3px;
  cursor: pointer;

  svg {
    height: 14px;
    width: 14px;

    fill: ${({ theme }) => theme.themeColors.text};
  }

  &:hover {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};

    * {
      color: ${({ theme }) => theme.themeColors.buttonText};
      fill: ${({ theme }) => theme.themeColors.buttonText};
    }
  }
`;
const AgentScriptOutputWrapper = styled.div`
  padding: 36px 0 0 0;

  max-height: 500px;
  overflow: auto;

  user-select: text;

  * {
    user-select: text;
  }
`;

const tool = AI_TOOLS.AGENT_FLOW;

const getAgentDefinition = async (id) => {
  const URL = `v1/genai_agent_script_definitions/${id}`;
  const { status, data } = await sendApiRequest('get', URL);
  logNetRequest(status, data);
  handle400Statuses(status);
  if (status === 200) {
    return data;
  } else {
    return null;
  }
};

const updateAgentDefinitionOnServer = async (id, agentName, jsonData) => {
  const URL = `v1/genai_agent_script_definitions/${id}`;
  const form = {
    id,
    definition_json: JSON.stringify(jsonData),
    script_name: agentName,
  };
  const { status, data } = await sendApiRequest('patch', URL, form);
  logNetRequest(status, data);
  handle400Statuses(status);
  if (status === 200) {
    return true;
  } else {
    // showErrorBanner('Something went wrong, please try again.', null, dispatch);
    return false;
  }
};

const sendAgentDefinitionToServer = async (name, jsonData) => {
  const URL = `v1/genai_agent_script_definitions`;
  const form = {
    definition_json: JSON.stringify(jsonData),
    script_name: name,
  };
  const { status, data } = await sendApiRequest('post', URL, form);
  logNetRequest(status, data);
  handle400Statuses(status);
  return { status, data };
};

const LoadAgent = ({ onLoad }) => {
  const [loadSelection, setLoadSelection] = React.useState('select');
  const [agents, setAgents] = React.useState([]);
  const [error, setError] = React.useState(null);

  const onLoadSelectionChange = (e) => {
    setLoadSelection(e.target.value);
  };

  const getAgents = async () => {
    setError(null);
    const URL = `v1/genai_agent_script_definitions`;
    const { status, data } = await sendApiRequest('get', URL);
    logNetRequest(status, data);
    handle400Statuses(status);
    if (status === 200) {
      const sortedData = data.sort((a, b) => a.script_name.localeCompare(b.script_name));
      setAgents([{ id: 'select', script_name: 'Select An Agent...' }, ...sortedData]);
      return true;
    } else {
      setError('Something went wrong, refresh and try again.');
      return false;
    }
  };

  const handleLoadAgent = () => {
    if (loadSelection !== 'select') {
      onLoad(loadSelection);
    }
  };

  React.useEffect(() => {
    getAgents();
  }, []);

  const onRetry = () => {
    getAgents();
  };

  if (error) {
    return (
      <Container>
        <Container>
          <Body5 bold>{error}</Body5>
        </Container>
        <Container top={16} style={{ textAlign: 'center' }}>
          <FlatButton fullWidth onClick={onRetry}>
            Reload Agents
          </FlatButton>
        </Container>
      </Container>
    );
  }

  return (
    <Container>
      <Container top={8}>
        <Body5 bold>Select Agent Script</Body5>
      </Container>
      <Container top={16}>
        <select onChange={onLoadSelectionChange}>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.script_name}
            </option>
          ))}
        </select>
      </Container>
      <LoadButtonWrapper>
        <FlatButton fullWidth onClick={handleLoadAgent}>
          Load
        </FlatButton>
      </LoadButtonWrapper>
    </Container>
  );
};

const AgentScriptOutput = ({ agentName, prompt, resultId }) => {
  const handleOpenInNewWindow = () => {
    window.open(`/agent-result?id=${resultId}`, '_blank');
  };

  return (
    <Container>
      <Container row verticallyCenter spaceBetween>
        <Body5 bold uppercase>
          Output for "{agentName}"
        </Body5>
        <Container right={24}>
          <TransparentFlatButton onClick={handleOpenInNewWindow}>Open In New Window</TransparentFlatButton>
        </Container>
      </Container>
      <Container top={24}>
        <ResultContextProvider resultId={resultId} isAgentScriptResult>
          <Container left={16}>
            <AgentStreamingResultStatus />
          </Container>
          <Container left={16}>
            <AgentAnswer />
          </Container>
          <Container top={28}>
            <AgentExecutedPlan />
          </Container>
        </ResultContextProvider>
      </Container>
      <Container top={8}></Container>
    </Container>
  );
};

const ReadOnlyBanner = styled.div`
  margin-top: 16px;
  padding: 16px 0;
  border-radius: 6px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const Content = ({
  data,
  resultId,
  prompt,
  onRun,
  onLoadStart,
  onLoad,
  onSave,
  onCreate,
  onDuplicate,
  onCreateStart,
}) => {
  const { mode, scriptName, agentId, message, isEditable = true } = data;
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [editedName, setEditedName] = React.useState(scriptName);

  React.useEffect(() => {
    setEditedName(scriptName);
  }, [scriptName]);

  const onEditNameStart = () => {
    setIsEditingName(true);
  };

  const onEditName = (e) => {
    setEditedName(e.target.value);
  };

  const onEditBlur = () => {
    setTimeout(() => {
      setIsEditingName(false);
      data.actions.updateNodeData(data.id, { scriptName: editedName });
    }, 100);
  };

  const onSaveCreatedName = () => {
    onCreate({ name: editedName, json: {} });
  };

  if (mode === 'running-query' || mode === 'saving') {
    return (
      <Container top={48}>
        <Working noVerticalPadding message={mode === 'saving' ? 'Saving' : null} />
      </Container>
    );
  }

  if (mode === 'created') {
    return (
      <Container>
        <Container top={16}>
          <Body5 bold>Enter Agent Name: </Body5>
          <Container top={16}>
            <AgentNameWrapper>
              <input type="text" value={editedName} onChange={onEditName} onBlur={onEditBlur} />
              <FlatButton onClick={onSaveCreatedName}>Save</FlatButton>
            </AgentNameWrapper>
          </Container>
        </Container>
      </Container>
    );
  }

  if (mode === 'loaded') {
    return (
      <Container>
        {message && (
          <Container top={16}>
            <Body5 bold>{message}</Body5>
          </Container>
        )}
        <Container top={16}>
          <AgentNameWrapper>
            <Body5 bold>Agent: </Body5>
            {isEditable && isEditingName ? (
              <input type="text" value={editedName} onChange={onEditName} onBlur={onEditBlur} />
            ) : isEditable ? (
              <EditAgentNameWrapper onClick={onEditNameStart}>
                <Body5 bold>{scriptName}</Body5>
                <EditIcon />
              </EditAgentNameWrapper>
            ) : (
              <Body5 bold>{scriptName}</Body5>
            )}
          </AgentNameWrapper>
        </Container>

        {!isEditable && (
          <ReadOnlyBanner>
            <Body5 bold>Read-only</Body5>
            <Body5 isLowContrast>This script can't be edited. Use Duplicate to make an editable copy.</Body5>
          </ReadOnlyBanner>
        )}

        <Container top={24} bottom={24}>
          <ButtonStyler>
            <FlatButton onClick={onRun}>Run</FlatButton>
            {isEditable && <FlatButton onClick={onSave}>Save</FlatButton>}
            <SkeletonButton onClick={onDuplicate}>Duplicate</SkeletonButton>
          </ButtonStyler>
        </Container>

        {resultId && (
          <AgentScriptOutputWrapper>
            <AgentScriptOutput agentName={scriptName} prompt={prompt} resultId={resultId} />
          </AgentScriptOutputWrapper>
        )}
      </Container>
    );
  }

  if (mode === 'load-select') {
    return (
      <Container>
        <LoadAgent onLoad={onLoad} />
      </Container>
    );
  }

  return (
    <Container>
      <Container bottom={16}>
        <Body5>Get started by creating a new script to start building, or load an existing one.</Body5>
      </Container>

      <ButtonStyler>
        <FlatButton onClick={onCreateStart}>Create</FlatButton>
        <FlatButton onClick={onLoadStart}>Load</FlatButton>
      </ButtonStyler>
    </Container>
  );
};

function updateNameForDuplication(name) {
  // Check if the name already ends with " (Copy)" or " (Copy N)"
  const copyPattern = /\s\(Copy(?:\s(\d+))?\)$/;
  const match = name.match(copyPattern);

  if (match) {
    // If there's already a number, increment it
    if (match[1]) {
      const currentNumber = parseInt(match[1], 10);
      const newNumber = currentNumber + 1;
      return name.replace(copyPattern, ` (Copy ${newNumber})`);
    } else {
      return name.replace(copyPattern, ' (Copy 1)');
    }
  } else {
    return `${name} (Copy)`;
  }
}

export default React.memo((props) => {
  const { data, id } = props;
  const startNodeId = id;
  const { actions, nodesRef, edgesRef } = data;

  const isEditable = useIsAgentScriptEditable();
  const hasQueryNode = useHasQueryNode();

  const updateNodeInternals = useUpdateNodeInternals();

  const dispatch = useDispatch();

  const [prompt, setPrompt] = React.useState(null);
  const [resultId, setResultId] = React.useState(null);
  const [error, setError] = React.useState(null);
  const hasAutoLoadedRef = React.useRef(false);

  React.useEffect(() => {
    // Resync node internals when handles change
    updateNodeInternals(id);
  }, [data.mode]);

  const updateData = (data) => {
    actions.updateNodeData(id, data);
  };

  const onLoad = async (id) => {
    const responseData = await getAgentDefinition(id);
    if (!responseData) {
      actions.onShowError('Something went wrong, please try again.');
      return;
    }

    const { id: agentId, script_name: agentName, definition_json, is_editable } = responseData;

    let json;
    try {
      json = JSON.parse(definition_json);
    } catch (e) {
      logger('Failed to parse agent definition_json', `agent ${agentId}`, e);
      actions.onShowError(
        `This agent script could not be loaded because its definition is invalid or corrupted. Please contact support if this persists.`,
      );
      return;
    }

    const isEditable = is_editable !== false;
    const agentScript = {
      id: agentId,
      name: agentName,
      json,
    };
    updateData({ mode: 'loaded', agentId, scriptName: agentName, json, isEditable });
    actions.setScriptIsEditable(isEditable);
    data.actions.createNodesAndEdgesForLoadedAgentScript(startNodeId, agentScript);
  };

  // Deep-link support: when opened with an `initialScriptName` (set from the
  // ?script= URL param), resolve that name to an id and load it once on mount.
  React.useEffect(() => {
    const scriptName = data.initialScriptName;
    if (!scriptName || hasAutoLoadedRef.current) return undefined;
    hasAutoLoadedRef.current = true;

    let isCanceled = false;
    const autoLoadByName = async () => {
      const scripts = await fetchAgentScripts();
      if (isCanceled) return;
      const match = findAgentScriptByName(scripts, scriptName);
      if (match) {
        onLoad(match.id);
      } else {
        actions.onShowError(`Couldn't find an agent script named "${scriptName}".`);
      }
    };
    autoLoadByName();

    return () => {
      isCanceled = true;
    };
  }, []);

  const getQueryNode = (nodes, edges) => {
    const queryNode = nodes.find((node) => node.type === NODE_TYPES.AgentScriptQueryNode);
    return queryNode;
  };

  const onSave = async () => {
    return new Promise((resolve) => {
      updateData({ mode: 'saving' });

      const generator = new AgentScriptGenerationHelpers(nodesRef.current, edgesRef.current);
      const json = generator.generateJsonFromNodesAndEdges();
      updateAgentDefinitionOnServer(data.agentId, data.scriptName, json).then((ok) => {
        updateData({ mode: 'loaded' });
        if (ok) {
          showSuccessBanner('Update saved successfully', null, dispatch);
          resolve();
        } else {
          resolve();
        }
      });
    });
  };

  const onRun = async () => {
    if (data.isEditable !== false) {
      await onSave();
    }

    const queryNode = getQueryNode(nodesRef.current, edgesRef.current);
    const question = queryNode?.data?.question;
    const docList = queryNode?.data?.selectedDocs;
    const advancedSettingsValues = queryNode?.data?.advancedSettingsValues;

    const docIds = docList?.length > 0 ? docList.map((doc) => doc.id) : undefined;
    const settings = { user_mode: tool };
    if (advancedSettingsValues) {
      addAdvancedSettingValues(advancedSettingsValues, settings);
    }

    setError(null);
    setResultId(null);
    setPrompt(question);

    TrackingEvent.create('Run Agent Script', {
      AgentName: data.scriptName,
      Question: question,
    });

    logger('onRun', { question, agentName: data.scriptName, docIds, settings });
    updateData({ mode: 'running-query' });
    submitQueriesStream({
      questions: question,
      agentName: data.scriptName,
      docIds,
      settings,
    }).then((result) => {
      if (result.status === 200) {
        setResultId(result.runId);
        updateData({ mode: 'loaded' });
        return result.data;
      } else {
        const message = result.error || 'Something went wrong, try again.';
        setError(message);
        updateData({ mode: 'loaded' });
        return { error: message };
      }
    });
  };

  const onCreateStart = () => {
    updateData({ mode: 'created' });
  };

  const createScript = async (agentScript) => {
    const { status, data } = await sendAgentDefinitionToServer(agentScript.name, agentScript.json);

    if (status === 200) {
      return { success: true, newId: data.id };
    } else {
      const error = data?.error || 'Something went wrong, please try again.';
      showErrorBanner(error, null, dispatch);
      return { error };
    }
  };

  const onCreate = async (agentScript) => {
    updateData({ mode: 'saving' });
    const { success, newId } = await createScript(agentScript);
    if (success) {
      updateData({
        mode: 'loaded',
        agentId: newId,
        scriptName: agentScript.name,
        json: agentScript.json,
        isEditable: true,
      });
      actions.setScriptIsEditable(true);
    }
  };

  const onLoadStart = () => {
    updateData({ mode: 'load-select' });
  };

  const onDuplicate = async () => {
    const generator = new AgentScriptGenerationHelpers(nodesRef.current, edgesRef.current);
    const currentJson = generator.generateJsonFromNodesAndEdges();
    const newAgentScript = {
      id: data.agentId,
      name: updateNameForDuplication(data.scriptName),
      json: currentJson,
    };

    updateData({ mode: 'saving' });
    const { success, newId } = await createScript(newAgentScript);
    if (success) {
      updateData({
        mode: 'loaded',
        agentId: newId,
        scriptName: newAgentScript.name,
        json: newAgentScript.json,
        isEditable: true,
      });
      actions.setScriptIsEditable(true);
    }
  };

  const name = 'Start';
  return (
    <NodeWrapper data-id={id} name={name} data={data} nodeData={props} excludeFromReadOnly>
      <NodeHeader id={id} name={name} Left={8} />
      {['created', 'loaded', 'running-query', 'saving'].includes(data.mode) && (
        // Query handle stays active even on read-only scripts (to create the runtime query node),
        // but only while no query node exists yet (max one).
        <QuerySourceHandle {...props} isConnectable={isEditable || !hasQueryNode} showName />
      )}
      {['created', 'loaded', 'running-query', 'saving'].includes(data.mode) && (
        <PlanStepsSourceHandle {...props} isConnectable={isEditable} showName />
      )}
      {['created', 'loaded', 'running-query', 'saving'].includes(data.mode) && (
        <OutputAggregationSourceHandle {...props} isConnectable={isEditable} showName />
      )}
      <ContentWrapper>
        {error && (
          <Container>
            <Body5>{error}</Body5>
          </Container>
        )}
        <Content
          data={data}
          onCreateStart={onCreateStart}
          onLoadStart={onLoadStart}
          onLoad={onLoad}
          onRun={onRun}
          onSave={onSave}
          onCreate={onCreate}
          onDuplicate={onDuplicate}
          prompt={prompt}
          resultId={resultId}
        />
      </ContentWrapper>
    </NodeWrapper>
  );
});
